import type { OrderStatus } from '@/bindings/OrderStatus'
import type { PlacedOrder } from '@/bindings/PlacedOrder'
import type { PlacedOrderLine } from '@/bindings/PlacedOrderLine'
import type { Translate } from '@/lib/i18n/translate'
import { fromSteps, toSteps } from '@/lib/quantity'

/** The happy path (T16); canceled and expired sit outside it. */
export const ORDER_STEPS: readonly OrderStatus[] = ['sent', 'confirmed', 'processing', 'ready', 'completed']

/** Every status, in state-machine order. */
export const ORDER_STATUSES: readonly OrderStatus[] = [...ORDER_STEPS, 'canceled', 'expired']

/** "Table X" or "Take-away": the one wording for where an order sits. */
export function whereLabel(t: Translate, tableName: string | null | undefined): string {
  return tableName == null ? t('order.takeAway') : t('order.table', { name: tableName })
}

type Returnable = Pick<PlacedOrderLine, 'quantity' | 'returned_quantity' | 'step'>

/** What is still on the line after returns, in the item's steps; undefined off the step (never from the server). */
export function remainingSteps(line: Returnable): number | undefined {
  const quantity = toSteps(line.quantity, line.step)
  const returned = toSteps(line.returned_quantity, line.step)
  return quantity === undefined || returned === undefined ? undefined : quantity - returned
}

/** `remainingSteps` as a quantity (`line_total` already prices it); the server's string if off the step. */
export function netQuantity(line: Returnable): string {
  const steps = remainingSteps(line)
  return steps === undefined ? line.quantity : fromSteps(steps, line.step)
}

/** Narrows a URL, form, or API value; anything else is `undefined`. */
export function parseOrderStatus(value: unknown): OrderStatus | undefined {
  return ORDER_STATUSES.find((candidate) => candidate === value)
}

/** T16: customers cancel from SENT or CONFIRMED only. */
export function customerCancelable(status: OrderStatus): boolean {
  return status === 'sent' || status === 'confirmed'
}

/**
 * Whether this phone may cancel `order` (G7): the account's own, or an
 * anonymous one whose code it holds — the URL on the order page, the
 * local-storage list (G6) on the session page. Another account's: never.
 */
export function ownsOrder(order: PlacedOrder, codesHeld: ReadonlySet<string>): boolean {
  if (order.ownership === 'caller') return true
  if (order.ownership === 'anonymous') return codesHeld.has(order.code)
  return false
}
